import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import {
  streamingAccountService,
  Slot,
  ESlotStatus,
  EStreamingAccountStatus,
} from "@/services/streaming-account";
import { Subscription, ESubscriptionStatus, EownerType, subscriptionService } from "@/services/subscription";
import {
  messageTemplateService,
  EMessageTemplateType,
} from "@/services/messageTemplate";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { ServiceBadge } from "@/components/dashboard/ServiceBadge";
import { ExtendSubscriptionModal } from "@/components/subscriptions/ExtendSubscriptionModal";
import { AssignSubscriptionModal } from "@/components/subscriptions/AssignSubscriptionModal";
import { RegisterSlotModal } from "@/components/streaming-accounts/RegisterSlotModal";
import { UpdateSlotModal } from "@/components/streaming-accounts/UpdateSlotModal";
import { UpdateStreamingAccountModal } from "@/components/streaming-accounts/UpdateStreamingAccountModal";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Edit,
  Ban,
  CheckCircle,
  UserPlus,
  UserMinus,
  Eye,
  EyeOff,
  Calendar,
  Mail,
  FileText,
  Loader2,
  ArrowLeft,
  Clock,
  DollarSign,
  Users,
  CreditCard,
  Plus,
  Pencil,
  Truck,
  ExternalLink,
  KeyRound,
  Bell,
} from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function mapSubscriptionStatus(status: ESubscriptionStatus): "ACTIVE" | "EXPIRING" | "EXPIRED" | "CANCELLED" {
  const statusMap: Record<ESubscriptionStatus, "ACTIVE" | "EXPIRING" | "EXPIRED" | "CANCELLED"> = {
    [ESubscriptionStatus.ACTIVE]: "ACTIVE",
    [ESubscriptionStatus.EXPIRING]: "EXPIRING",
    [ESubscriptionStatus.EXPIRED]: "EXPIRED",
    [ESubscriptionStatus.CANCELLED]: "CANCELLED",
  };
  return statusMap[status] || "ACTIVE";
}

interface SlotCardProps {
  slot: Slot;
  subscriptions: Subscription[];
  onAssignSlot: (slot: Slot) => void;
  onViewSubscription: (subscriptionId: string) => void;
  onEditSlot: (slot: Slot) => void;
}

function SlotCard({ slot, subscriptions, onAssignSlot, onViewSubscription, onEditSlot }: SlotCardProps) {
  const slotSubscription = subscriptions.find(s => s.slotId === slot.id);
  const isExpiringSoon = slot.subscriptionEndDate &&
    new Date(slot.subscriptionEndDate).getTime() - new Date().getTime() < 3 * 24 * 60 * 60 * 1000;

  return (
    <div
      className={cn(
        "p-4 rounded-xl border transition-all",
        slot.status === ESlotStatus.AVAILABLE
          ? "bg-success/5 border-success/20"
          : isExpiringSoon
            ? "bg-warning/5 border-warning/30"
            : "bg-card border-border"
      )}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={cn(
            "slot-indicator",
            slot.status === ESlotStatus.AVAILABLE ? "slot-available" : "slot-occupied"
          )} />
          <span className="font-semibold text-foreground">{slot.name}</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => onEditSlot(slot)}
              >
                <Pencil className="h-3 w-3 text-muted-foreground" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Editar slot</TooltipContent>
          </Tooltip>
        </div>

        {slot.status === ESlotStatus.AVAILABLE ? (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-success/10 text-success">
            Disponible
          </span>
        ) : (
          <span className={cn(
            "px-2 py-1 text-xs font-medium rounded-full",
            isExpiringSoon
              ? "bg-warning/10 text-warning"
              : "bg-destructive/10 text-destructive"
          )}>
            {isExpiringSoon ? "Por vencer" : "Ocupado"}
          </span>
        )}
      </div>

      {slot.pin && (
        <div className="flex items-center gap-2 text-sm mb-2">
          <span className="text-muted-foreground">PIN:</span>
          <span className="font-medium text-foreground font-mono tracking-wider">{slot.pin}</span>
        </div>
      )}

      {slot.status === ESlotStatus.OCCUPIED && (
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Cliente:</span>
            <span className="font-medium text-foreground">{slot?.client?.name}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Vence:</span>
            <span className={cn(
              "font-medium",
              isExpiringSoon ? "text-warning" : "text-foreground"
            )}>
              {slot.subscriptionEndDate && new Date(slot.subscriptionEndDate).toLocaleDateString("es-CO")}
            </span>
          </div>
          {slot.price && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Precio:</span>
              <span className="font-semibold text-foreground">${formatCurrency(slot.price)}</span>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 pt-2 border-t border-border">
        {slot.status === ESlotStatus.AVAILABLE ? (
          <Button size="sm" className="w-full gap-2" onClick={() => onAssignSlot(slot)}>
            <UserPlus className="h-4 w-4" />
            Asignar Cliente
          </Button>
        ) : (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 gap-2"
                  onClick={() => slotSubscription && onViewSubscription(slotSubscription.id)}
                  disabled={!slotSubscription}
                >
                  <Eye className="h-4 w-4" />
                  Ver
                </Button>
              </TooltipTrigger>
              <TooltipContent>Ver suscripción</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <UserMinus className="h-4 w-4 text-destructive" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Liberar slot</TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
}

export default function AccountDetail() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showPassword, setShowPassword] = useState(false);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [isEditAccountModalOpen, setIsEditAccountModalOpen] = useState(false);
  const [isAddSlotModalOpen, setIsAddSlotModalOpen] = useState(false);
  const [editSlot, setEditSlot] = useState<Slot | null>(null);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [assignSlot, setAssignSlot] = useState<Slot | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  // Obtener cuenta de streaming (incluye subscriptions)
  const {
    data: accountResponse,
    isLoading: isLoadingAccount,
    error: accountError,
  } = useQuery({
    queryKey: ["streaming-account", accountId],
    queryFn: () => streamingAccountService.getOne(accountId!),
    enabled: !!accountId,
  });

  const account = accountResponse?.data;
  const isInactive = account?.status === EStreamingAccountStatus.INACTIVE;

  // Obtener suscripciones de la cuenta de streaming
  const {
    data: subscriptionsResponse,
    isLoading: isLoadingSubscriptions,
  } = useQuery({
    queryKey: ["subscriptions", { streamingAccountId: accountId }],
    queryFn: () =>
      subscriptionService.getAll({
        streamingAccountId: Number(accountId),
        limit: 100,
      }),
    enabled: !!accountId,
  });

  const subscriptions = subscriptionsResponse?.data || [];
  const clientSubscriptions = subscriptions.filter(s => s.ownerType !== EownerType.SUPPLIER);
  const providerSubscriptions = subscriptions.filter(s => s.ownerType === EownerType.SUPPLIER);

  // Plantillas activas para saber a cuál enviar según el tipo de mensaje
  const { data: templatesResponse } = useQuery({
    queryKey: ["message-templates", "active"],
    queryFn: () => messageTemplateService.getAll({ isActive: true }),
  });

  const templates = templatesResponse?.data || [];
  const accountInfoTemplate = templates.find(
    (tpl) => tpl.type === EMessageTemplateType.INFO_ACCOUNT,
  );
  const paymentReminderTemplate = templates.find(
    (tpl) => tpl.type === EMessageTemplateType.PAYMENT_REMINDER,
  );

  const sendReminderMutation = useMutation({
    mutationFn: (templateId: number) =>
      messageTemplateService.send(templateId, {
        streamingAccountId: Number(accountId),
      }),
    onSuccess: () => toast.success("Recordatorio enviado a todas las suscripciones de la cuenta"),
    onError: (error: Error) => toast.error(error.message),
  });

  const sendAccountInfoMutation = useMutation({
    mutationFn: (templateId: number) =>
      messageTemplateService.send(templateId, {
        streamingAccountId: Number(accountId),
      }),
    onSuccess: () => toast.success("Información de cuenta enviada a todas las suscripciones de la cuenta"),
    onError: (error: Error) => toast.error(error.message),
  });

  const handleSendReminder = () => {
    if (!paymentReminderTemplate) {
      toast.error("No hay una plantilla activa de tipo 'Recordatorio de pago'");
      return;
    }
    sendReminderMutation.mutate(paymentReminderTemplate.id);
  };

  const handleSendAccountInfo = () => {
    if (!accountInfoTemplate) {
      toast.error("No hay una plantilla activa de tipo 'Información de cuenta'");
      return;
    }
    sendAccountInfoMutation.mutate(accountInfoTemplate.id);
  };

  const toggleStatusMutation = useMutation({
    mutationFn: (newStatus: EStreamingAccountStatus) =>
      streamingAccountService.update(accountId!, { status: newStatus }),
    onSuccess: (_, newStatus) => {
      toast.success(
        newStatus === EStreamingAccountStatus.INACTIVE
          ? "Cuenta desactivada correctamente"
          : "Cuenta activada correctamente"
      );
      queryClient.invalidateQueries({ queryKey: ["streaming-account", accountId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al actualizar el estado de la cuenta");
    },
  });

  const handleStatusButtonClick = () => {
    if (!isInactive) {
      const occupiedSlots = account?.slots.filter(s => s.status === ESlotStatus.OCCUPIED) ?? [];
      if (occupiedSlots.length > 0) {
        toast.error(`No se puede desactivar: hay ${occupiedSlots.length} slot(s) ocupado(s)`);
        return;
      }
    }
    setIsStatusDialogOpen(true);
  };

  const handleConfirmStatusChange = () => {
    const newStatus = isInactive
      ? EStreamingAccountStatus.ACTIVE
      : EStreamingAccountStatus.INACTIVE;
    toggleStatusMutation.mutate(newStatus);
  };

  const handleAssignSlot = (slot: Slot) => {
    setAssignSlot(slot);
    setIsAssignModalOpen(true);
  };

  const handleEditSlot = (slot: Slot) => {
    setEditSlot(slot);
  };

  const handleViewSubscription = (subscriptionId: string) => {
    navigate(`/subscriptions/${subscriptionId}`);
  };

  const handleExtendSubscription = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setIsExtendModalOpen(true);
  };

  const isLoading = isLoadingAccount || isLoadingSubscriptions;

  if (isLoading) {
    return (
      <DashboardLayout title="Detalle de Cuenta" subtitle="Cargando información...">
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Cargando...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (accountError || !account) {
    return (
      <DashboardLayout title="Cuenta no encontrada" subtitle="">
        <div className="card-elevated p-8 text-center">
          <p className="text-muted-foreground">La cuenta solicitada no existe.</p>
          <Button variant="link" asChild className="mt-2">
            <Link to="/accounts">Volver a Cuentas</Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const usedSlots = account.slots.filter(s => s.status === ESlotStatus.OCCUPIED).length;
  const availableSlots = account.totalSlots - usedSlots;
  const usagePercent = (usedSlots / account.totalSlots) * 100;

  // Calcular estadísticas (basadas en suscripciones de clientes)
  const activeSubscriptions = clientSubscriptions.filter(s => s.status === ESubscriptionStatus.ACTIVE || s.status === ESubscriptionStatus.EXPIRING);
  const monthlyRevenue = activeSubscriptions.reduce((sum, s) => Number(sum) + Number(s.price), 0);
  const uniqueClients = [...new Set(clientSubscriptions.map(s => s.clientId))].length;

  return (
    <DashboardLayout
      title=""
      subtitle=""
      customHeader={
        <div className="space-y-4">
          {/* Breadcrumb */}
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/accounts">Cuentas</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{account.email}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <ServiceBadge icon={account.service.icon} name={account.service.name}  />
              <div>
                <h1 className="text-2xl font-bold text-foreground">{account.email}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-muted-foreground">{account.service.name}</span>
                  <span className="text-muted-foreground">•</span>
                  <StatusBadge status={account.status} />
                  {account.ownerName && (
                    <>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-primary text-sm">Dedicada: {account.ownerName}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="gap-2 border-green-500/40 text-green-500 hover:bg-green-500/10 hover:border-green-500/60 hover:text-green-500"
                    onClick={handleSendReminder}
                    disabled={sendReminderMutation.isPending || !paymentReminderTemplate}
                  >
                    {sendReminderMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Bell className="h-4 w-4" />
                    )}
                    Enviar recordatorio
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Envía el recordatorio de pago a todas las suscripciones activas de esta cuenta.
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="gap-2 border-green-500/40 text-green-500 hover:bg-green-500/10 hover:border-green-500/60 hover:text-green-500"
                    onClick={handleSendAccountInfo}
                    disabled={sendAccountInfoMutation.isPending || !accountInfoTemplate}
                  >
                    {sendAccountInfoMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <KeyRound className="h-4 w-4" />
                    )}
                    Enviar información
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Envía la información de la cuenta (email/contraseña) a todas las suscripciones activas de esta cuenta.
                </TooltipContent>
              </Tooltip>
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => setIsEditAccountModalOpen(true)}
              >
                <Edit className="h-4 w-4" />
                Editar
              </Button>
              <Button
                variant="outline"
                className={cn(
                  "gap-2",
                  isInactive
                    ? "text-success hover:text-success"
                    : "text-destructive hover:text-destructive"
                )}
                onClick={handleStatusButtonClick}
                disabled={toggleStatusMutation.isPending}
              >
                {toggleStatusMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isInactive ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <Ban className="h-4 w-4" />
                )}
                {isInactive ? "Activar" : "Desactivar"}
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div className="space-y-6 animate-fade-in">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Slots Totales</p>
                  <p className="text-2xl font-bold text-foreground">{account.totalSlots}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Clientes Activos</p>
                  <p className="text-2xl font-bold text-foreground">{uniqueClients}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Suscripciones Activas</p>
                  <p className="text-2xl font-bold text-foreground">{activeSubscriptions.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Ingreso Mensual</p>
                  <p className="text-2xl font-bold text-foreground">${formatCurrency(monthlyRevenue)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Account Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Información de la Cuenta</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <ServiceBadge icon={account.service.icon} name={account.service.name } showName={false} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Servicio</p>
                  <p className="font-medium text-foreground">{account.service.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium text-foreground">{account.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Creada</p>
                  <p className="font-medium text-foreground">
                    {new Date(account.createdAt).toLocaleDateString("es-CO")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tipo</p>
                  <p className="font-medium text-foreground">
                    {account.type === "DEDICATED" ? "Dedicada" : "Compartida"}
                  </p>
                </div>
              </div>

              {account.password && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <KeyRound className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-muted-foreground">Contraseña</p>
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-foreground font-mono tracking-wider truncate">
                        {showPassword ? account.password : "•".repeat(Math.min(account.password.length, 10))}
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword
                          ? <EyeOff className="h-3.5 w-3.5" />
                          : <Eye className="h-3.5 w-3.5" />
                        }
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {account.supplier && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Truck className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-muted-foreground">Proveedor</p>
                    <button
                      type="button"
                      onClick={() => navigate(`/suppliers/${account.supplier!.id}`)}
                      className="flex items-center gap-1.5 font-medium text-primary hover:underline focus:outline-none group"
                    >
                      <span className="truncate">{account.supplier.name}</span>
                      <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
                    </button>
                    {account.supplier.phone && (
                      <p className="text-xs text-muted-foreground truncate">
                        {account.supplier.phone}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Slot Usage Summary */}
        <div className="card-elevated p-5">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h2 className="font-semibold text-lg text-foreground">Slots de Perfiles</h2>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="slot-indicator slot-occupied" />
                  <span className="text-muted-foreground">Usados: <span className="font-semibold text-foreground">{usedSlots}</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="slot-indicator slot-available" />
                  <span className="text-muted-foreground">Disponibles: <span className="font-semibold text-success">{availableSlots}</span></span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setIsAddSlotModalOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Agregar Slot
              </Button>
            </div>
          </div>

          <div className="mb-2">
            <Progress
              value={usagePercent}
              className={cn(
                "h-3",
                usagePercent >= 90 ? "[&>div]:bg-destructive" :
                  usagePercent >= 70 ? "[&>div]:bg-warning" : ""
              )}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {usedSlots} de {account.totalSlots} slots ocupados ({Math.round(usagePercent)}%)
          </p>
        </div>

        {/* Slots Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {account.slots.map((slot) => (
            <SlotCard
              key={slot.id}
              slot={slot}
              subscriptions={clientSubscriptions}
              onAssignSlot={handleAssignSlot}
              onViewSubscription={handleViewSubscription}
              onEditSlot={handleEditSlot}
            />
          ))}
        </div>

        {/* Client Subscriptions Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Suscripciones de Clientes ({clientSubscriptions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {clientSubscriptions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No hay suscripciones de clientes en esta cuenta</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Slot</TableHead>
                      <TableHead>Inicio</TableHead>
                      <TableHead>Fin</TableHead>
                      <TableHead className="text-center">Días Rest.</TableHead>
                      <TableHead className="text-right">Precio</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientSubscriptions.map((subscription) => (
                      <TableRow key={subscription.id}>
                        <TableCell className="font-medium">{subscription.client?.name}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {subscription.subscriptionItems?.[0]?.slot?.name || "Cuenta completa"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(subscription.startDate).toLocaleDateString("es-CO")}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(subscription.endDate).toLocaleDateString("es-CO")}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={cn(
                            "inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold",
                            subscription.daysRemaining === 0 && "bg-destructive text-destructive-foreground",
                            subscription.daysRemaining <= 2 && subscription.daysRemaining > 0 && "bg-warning text-warning-foreground",
                            subscription.daysRemaining > 2 && "bg-muted text-muted-foreground"
                          )}>
                            {subscription.daysRemaining}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          ${formatCurrency(subscription.price)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={mapSubscriptionStatus(subscription.status)} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {subscription.status !== ESubscriptionStatus.CANCELLED && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => handleExtendSubscription(subscription)}
                                  >
                                    <Clock className="h-4 w-4 text-primary" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Extender</TooltipContent>
                              </Tooltip>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleViewSubscription(subscription.id)}
                                >
                                  <Eye className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Ver detalle</TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Provider Subscription Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Truck className="h-5 w-5 text-primary" />
              Suscripción del Proveedor ({providerSubscriptions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {providerSubscriptions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No hay suscripción de proveedor registrada para esta cuenta</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Proveedor</TableHead>
                      <TableHead>Inicio</TableHead>
                      <TableHead>Fin</TableHead>
                      <TableHead className="text-center">Días Rest.</TableHead>
                      <TableHead className="text-right">Precio</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {providerSubscriptions.map((subscription) => (
                      <TableRow key={subscription.id}>
                        <TableCell className="font-medium">
                          {subscription.supplier ? (
                            <button
                              type="button"
                              className="flex items-center gap-1 text-primary hover:underline focus:outline-none group"
                              onClick={() => navigate(`/suppliers/${subscription.supplier!.id}`)}
                            >
                              <span>{subscription.supplier.name}</span>
                              <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                            </button>
                          ) : (
                            subscription.supplierId ? `Proveedor #${subscription.supplierId}` : "—"
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(subscription.startDate).toLocaleDateString("es-CO")}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(subscription.endDate).toLocaleDateString("es-CO")}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={cn(
                            "inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold",
                            subscription.daysRemaining === 0 && "bg-destructive text-destructive-foreground",
                            subscription.daysRemaining <= 2 && subscription.daysRemaining > 0 && "bg-warning text-warning-foreground",
                            subscription.daysRemaining > 2 && "bg-muted text-muted-foreground"
                          )}>
                            {subscription.daysRemaining}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          ${formatCurrency(subscription.price)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={mapSubscriptionStatus(subscription.status)} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {subscription.status !== ESubscriptionStatus.CANCELLED && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => handleExtendSubscription(subscription)}
                                  >
                                    <Clock className="h-4 w-4 text-primary" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Extender</TooltipContent>
                              </Tooltip>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleViewSubscription(subscription.id)}
                                >
                                  <Eye className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Ver detalle</TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal Editar Cuenta */}
      <UpdateStreamingAccountModal
        open={isEditAccountModalOpen}
        onOpenChange={setIsEditAccountModalOpen}
        account={account}
      />

      {/* Modal Extender Suscripción */}
      <ExtendSubscriptionModal
        open={isExtendModalOpen}
        onOpenChange={setIsExtendModalOpen}
        subscription={selectedSubscription}
      />

      {/* Modal Registrar Slot */}
      <RegisterSlotModal
        open={isAddSlotModalOpen}
        onOpenChange={setIsAddSlotModalOpen}
        accountId={accountId!}
        accountEmail={account.email}
      />

      {/* Modal Actualizar Slot */}
      <UpdateSlotModal
        open={!!editSlot}
        onOpenChange={(open) => !open && setEditSlot(null)}
        slot={editSlot}
        accountId={accountId}
      />

      {/* Modal Asignar Suscripción */}
      <AssignSubscriptionModal
        open={isAssignModalOpen}
        onOpenChange={(open) => {
          setIsAssignModalOpen(open);
          if (!open) setAssignSlot(null);
        }}
        slotId={assignSlot?.id ?? 0}
        slotName={assignSlot?.name ?? ""}
        streamingAccountId={account.id}
        serviceId={account.serviceId}
      />

      {/* Diálogo confirmación activar/desactivar */}
      <AlertDialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isInactive ? "¿Activar cuenta?" : "¿Desactivar cuenta?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isInactive
                ? `La cuenta ${account.email} pasará a estado activo y podrá recibir nuevas suscripciones.`
                : `La cuenta ${account.email} pasará a estado inactivo. No podrá recibir nuevas suscripciones mientras esté desactivada.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmStatusChange}
              className={isInactive ? "" : "bg-destructive hover:bg-destructive/90"}
            >
              {isInactive ? "Sí, activar" : "Sí, desactivar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
